import { styled, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ErrorIcon from '@mui/icons-material/Error';
import { Icon } from '../../atoms/Icon/Icon';
import { BasicButton } from '../../atoms/BasicButton/BasicButton';
import { FEATURE_NAMES } from '../../../common/constants';
import { checkFFEnabled } from '../../../utils';

// Shared modal stacking: each open modal takes the next z-index above the tooltip layer, so modals
// layer in the order they open. The sequence resets once every modal has closed.
let openModalCount = 0;
let modalOpenSequence = 0;

export const useStackedModalZIndex = (open: boolean): number => {
    const theme = useTheme();
    const [zIndex, setZIndex] = useState(theme.zIndex.tooltip + 1);
    useEffect(() => {
        if (!open) return;
        openModalCount += 1;
        modalOpenSequence += 1;
        setZIndex(theme.zIndex.tooltip + modalOpenSequence);
        return () => {
            openModalCount -= 1;
            if (openModalCount === 0) modalOpenSequence = 0;
        };
    }, [open, theme.zIndex.tooltip]);
    return zIndex;
};

const Background = styled('div')<{ width: string }>(({ theme, width }) => ({
    width,
    maxWidth: '100%',
    position: 'fixed',
    top: 0,
    right: 0,
    background: 'rgba(191,191,191,0.8)',
    zIndex: theme.zIndex.tooltip + 1,
    height: '100%',
    padding: '79px 23px'
}));
const BackgroundDialogCentered = styled('div')<{ width: string }>(({ theme, width }) => ({
    width,
    maxWidth: '100%',
    position: 'fixed',
    top: 0,
    right: 0,
    background: 'rgba(191,191,191,0.8)',
    zIndex: theme.zIndex.tooltip + 1,
    height: '100%',
    padding: '79px 23px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
}));

const BackgroundCentered = styled('div')(({ theme }) => ({
    top: 0,
    height: '100%',
    width: '100%',
    position: 'fixed',
    background: 'rgba(191,191,191,0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: theme.zIndex.tooltip + 1,
    right: 0
}));

const Wrapper = styled('div')(() => ({
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)'
}));

const Card = styled('div')<{ cardMaxWidth: string }>(({ cardMaxWidth, theme }) => ({
    display: 'flex',
    padding: '40px',
    flexDirection: 'column',
    background: theme.palette.neutral.white,
    position: 'relative',
    maxWidth: cardMaxWidth,
    margin: 'auto',
    border: `1px solid ${theme.palette.semantic.bd.default}`,
    borderRadius: '12px',
    '> svg': {
        marginBottom: '24px',
        color: theme.palette.orange[300],
        width: '28px',
        height: '28px'
    }
}));

const CloseButton = styled('button')(() => ({
    display: 'flex',
    width: '48px',
    height: '48px',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    position: 'absolute',
    top: 12,
    right: 12,
    padding: 0,
    margin: 0
}));

const Title = styled('p')(({ theme }) => ({
    all: 'unset',
    color: theme.palette.primary[800],
    fontFamily: 'TT Commons Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '20px',
    marginBottom: '8px'
}));

const Content = styled('p')(({ theme }) => ({
    all: 'unset',
    color: theme.palette.semantic.text.default,
    fontFamily: 'TT Commons Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.16px',
    marginBottom: '18px'
}));

const ButtonsContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '29px'
}));

const StyledButton = styled('button')(({ theme }) => ({
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    color: theme.palette.primary[500],
    fontFamily: 'TT Commons Pro',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '18px',
    textDecorationLine: 'underline',
    padding: 0,
    margin: 0,
    textUnderlineOffset: '4px'
}));

// AEX-3428 (FF New_Tooltip_FTDS): FTDS confirmation modal — red error mark, bold title, muted body
// and a filled Proceed / outlined Cancel button pair.
const NewCard = styled('div')<{ cardMaxWidth: string }>(({ cardMaxWidth, theme }) => ({
    display: 'flex',
    padding: '24px',
    flexDirection: 'column',
    background: theme.palette.neutral.white,
    position: 'relative',
    maxWidth: cardMaxWidth,
    minWidth: '320px',
    margin: 'auto',
    borderRadius: '12px',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)'
}));

const NewTitle = styled('p')(({ theme }) => ({
    all: 'unset',
    color: theme.palette.primary[800],
    fontFamily: 'TT Commons Pro',
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '24px',
    marginTop: '16px',
    marginBottom: '8px'
}));

const NewContent = styled('p')(({ theme }) => ({
    all: 'unset',
    color: theme.palette.semantic.text.default,
    fontFamily: 'TT Commons Pro',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    marginBottom: '24px'
}));

const NewButtonsContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px'
}));

export interface ConfirmationDialogProps {
    open: boolean;
    content: React.ReactNode;
    title?: string;
    width?: string;
    cardMaxWidth?: string;
    confirmButtonLabel?: string;
    cancelButtonLabel?: string;
    onCancellation: () => void;
    onConfirmation: () => void;
    onCloseIconClick: () => void;
    cancelButtonFirst?: boolean;
    hideClose?: boolean;
    center?: boolean;
}

export const ConfirmationDialog = ({
    content,
    confirmButtonLabel = 'Confirm',
    cancelButtonLabel = 'Cancel',
    onConfirmation,
    onCancellation,
    onCloseIconClick,
    open = false,
    title,
    width = '400px',
    cardMaxWidth = '567px',
    hideClose = false,
    center = false
}: ConfirmationDialogProps) => {
    const zIndex = useStackedModalZIndex(open);
    if (!open) {
        return null;
    }

    if (center) {
        return createPortal(
            <BackgroundDialogCentered width={width} style={{ zIndex }}>
                <ConfirmationContent
                    cancelButtonLabel={cancelButtonLabel}
                    confirmButtonLabel={confirmButtonLabel}
                    content={content}
                    onCancellation={onCancellation}
                    onConfirmation={onConfirmation}
                    onCloseIconClick={onCloseIconClick}
                    open
                    title={title}
                    cardMaxWidth={cardMaxWidth}
                    hideClose={hideClose}
                />
            </BackgroundDialogCentered>,
            document.body
        );
    }
    return createPortal(
        <Background width={width} style={{ zIndex }}>
            <ConfirmationContent
                cancelButtonLabel={cancelButtonLabel}
                confirmButtonLabel={confirmButtonLabel}
                content={content}
                onCancellation={onCancellation}
                onConfirmation={onConfirmation}
                onCloseIconClick={onCloseIconClick}
                open
                title={title}
                cardMaxWidth={cardMaxWidth}
                hideClose={hideClose}
            />
        </Background>,
        document.body
    );
};

export const ConfirmationCentered = ({
    content,
    confirmButtonLabel = 'Confirm',
    cancelButtonLabel = 'Cancel',
    onConfirmation,
    onCancellation,
    onCloseIconClick,
    open = false,
    cardMaxWidth = '25vw',
    title,
    cancelButtonFirst = false
}: ConfirmationDialogProps) => {
    const zIndex = useStackedModalZIndex(open);
    if (!open) {
        return null;
    }

    return createPortal(
        <BackgroundCentered style={{ zIndex }}>
            <Wrapper>
                <ConfirmationContent
                    cancelButtonLabel={cancelButtonLabel}
                    confirmButtonLabel={confirmButtonLabel}
                    content={content}
                    onCancellation={onCancellation}
                    onConfirmation={onConfirmation}
                    onCloseIconClick={onCloseIconClick}
                    cardMaxWidth={cardMaxWidth}
                    open
                    title={title}
                    cancelButtonFirst={cancelButtonFirst}
                />
            </Wrapper>
        </BackgroundCentered>,
        document.body
    );
};

const ConfirmationContent = ({
    content,
    confirmButtonLabel = 'Confirm',
    cancelButtonLabel = 'Cancel',
    onConfirmation,
    onCancellation,
    onCloseIconClick,
    open = false,
    title,
    cardMaxWidth = '567px',
    hideClose,
    cancelButtonFirst = false
}: ConfirmationDialogProps) => {
    if (!open) {
        return null;
    }

    if (checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])) {
        return (
            <NewCard cardMaxWidth={cardMaxWidth}>
                {!hideClose && (
                    <CloseButton type="button" onClick={onCloseIconClick}>
                        <CloseIcon color="inherit" />
                    </CloseButton>
                )}

                <ErrorIcon
                    sx={(theme) => ({ color: theme.palette.semantic.int.negative, width: '28px', height: '28px' })}
                />

                {!!title && <NewTitle>{title}</NewTitle>}
                <NewContent>{content}</NewContent>

                <NewButtonsContainer>
                    <BasicButton
                        variant="outlined"
                        onClick={onCancellation}
                        sx={(theme) => ({
                            borderRadius: '100px',
                            textTransform: 'none',
                            padding: '6px 20px',
                            color: theme.palette.semantic.int.selected,
                            borderColor: theme.palette.semantic.bd.default,
                            '&:hover': {
                                borderColor: theme.palette.semantic.bd.strong,
                                backgroundColor: 'transparent'
                            }
                        })}
                    >
                        {cancelButtonLabel}
                    </BasicButton>
                    <BasicButton
                        variant="contained"
                        color="primary"
                        onClick={onConfirmation}
                        sx={{ borderRadius: '100px', textTransform: 'none', padding: '6px 20px', boxShadow: 'none' }}
                    >
                        {confirmButtonLabel}
                    </BasicButton>
                </NewButtonsContainer>
            </NewCard>
        );
    }

    return (
        <Card cardMaxWidth={cardMaxWidth}>
            {!hideClose && (
                <CloseButton type="button" onClick={onCloseIconClick}>
                    <CloseIcon color="inherit" />
                </CloseButton>
            )}

            <Icon id="warning" />

            {!!title && <Title>{title}</Title>}
            <Content>{content}</Content>

            <ButtonsContainer>
                {cancelButtonFirst ? (
                    <>
                        <StyledButton type="button" onClick={onCancellation}>
                            {cancelButtonLabel}
                        </StyledButton>
                        <StyledButton type="button" onClick={onConfirmation}>
                            {confirmButtonLabel}
                        </StyledButton>
                    </>
                ) : (
                    <>
                        <StyledButton type="button" onClick={onConfirmation}>
                            {confirmButtonLabel}
                        </StyledButton>
                        <StyledButton type="button" onClick={onCancellation}>
                            {cancelButtonLabel}
                        </StyledButton>
                    </>
                )}
            </ButtonsContainer>
        </Card>
    );
};
